/**
 * KsefSyncScheduleEditor — szczegółowy harmonogram automatycznej synchronizacji KSeF
 * dla jednego połączenia (Faza 5). Zapisuje `syncConfig` przez PUT /ksef/connections/[id].
 *
 * Sterowanie: włącznik auto-sync, częstotliwość, kierunki, data „synchronizuj od",
 * okno godzin (w strefie czasowej tenanta — Ustawienia → „Ogólne”) i dni tygodnia. Cron honoruje
 * te ustawienia (shouldSync).
 *
 * Teksty przez i18n (namespace `tenant.ksefSchedule`). Mapy stałych trzymają KLUCZE i18n
 * (są poza komponentem — brak dostępu do `t`); tłumaczenie w miejscu użycia przez `t(MAP[x])`.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { Button } from '@/components/Button';
import { DatePicker } from '@/components/DatePicker';
import { Dropdown } from '@/components/Dropdown';
import { useToast } from '@/contexts/ToastContext';
import { api } from '@/lib/api';

export interface KsefSyncConfig {
  auto_sync_enabled?: boolean;
  sync_interval_minutes?: number;
  sync_types?: string[];
  sync_from_date?: string | null;
  active_hour_from?: number | null;
  active_hour_to?: number | null;
  active_weekdays?: number[] | null;
}

interface Props {
  connectionId: string;
  syncConfig?: KsefSyncConfig | null;
}

const INTERVAL_OPTIONS: Array<{ value: number; labelKey: string }> = [
  { value: 15, labelKey: 'tenant.ksefSchedule.intervals.min15' },
  { value: 30, labelKey: 'tenant.ksefSchedule.intervals.min30' },
  { value: 60, labelKey: 'tenant.ksefSchedule.intervals.hour1' },
  { value: 120, labelKey: 'tenant.ksefSchedule.intervals.hour2' },
  { value: 240, labelKey: 'tenant.ksefSchedule.intervals.hour4' },
  { value: 480, labelKey: 'tenant.ksefSchedule.intervals.hour8' },
  { value: 720, labelKey: 'tenant.ksefSchedule.intervals.hour12' },
  { value: 1440, labelKey: 'tenant.ksefSchedule.intervals.daily' },
];

// Kolejność wyświetlania: pon…niedz; wartości = getDay (0=niedz).
const WEEKDAYS: Array<{ value: number; labelKey: string }> = [
  { value: 1, labelKey: 'tenant.ksefSchedule.days.mon' },
  { value: 2, labelKey: 'tenant.ksefSchedule.days.tue' },
  { value: 3, labelKey: 'tenant.ksefSchedule.days.wed' },
  { value: 4, labelKey: 'tenant.ksefSchedule.days.thu' },
  { value: 5, labelKey: 'tenant.ksefSchedule.days.fri' },
  { value: 6, labelKey: 'tenant.ksefSchedule.days.sat' },
  { value: 0, labelKey: 'tenant.ksefSchedule.days.sun' },
];

const labelCls = 'mb-1 block text-xs font-medium text-text-secondary';

export function KsefSyncScheduleEditor({ connectionId, syncConfig }: Props) {
  const { t } = useTranslation();
  const toast = useToast();
  const qc = useQueryClient();
  const cfg = syncConfig ?? {};

  const [enabled, setEnabled] = useState(cfg.auto_sync_enabled ?? false);
  const [interval, setInterval] = useState(cfg.sync_interval_minutes ?? 60);
  const [types, setTypes] = useState<string[]>(cfg.sync_types ?? ['purchase', 'sales']);
  const [fromDate, setFromDate] = useState(cfg.sync_from_date ?? '');
  const [hourFrom, setHourFrom] = useState<string>(
    cfg.active_hour_from != null ? String(cfg.active_hour_from) : '',
  );
  const [hourTo, setHourTo] = useState<string>(
    cfg.active_hour_to != null ? String(cfg.active_hour_to) : '',
  );
  const [weekdays, setWeekdays] = useState<number[]>(cfg.active_weekdays ?? []);

  const toggleType = (ty: string) =>
    setTypes((prev) => (prev.includes(ty) ? prev.filter((x) => x !== ty) : [...prev, ty]));
  const toggleWeekday = (d: number) =>
    setWeekdays((prev) => (prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d]));

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload: KsefSyncConfig = {
        auto_sync_enabled: enabled,
        sync_interval_minutes: interval,
        sync_types: types,
        sync_from_date: fromDate || null,
        active_hour_from: hourFrom === '' ? null : Number(hourFrom),
        active_hour_to: hourTo === '' ? null : Number(hourTo),
        active_weekdays: weekdays.length > 0 ? weekdays : null,
      };
      return api.put(`/ksef/connections/${connectionId}`, { syncConfig: payload });
    },
    onSuccess: () => {
      toast.show({ title: t('tenant.ksefSchedule.toast.saved'), variant: 'success' });
      qc.invalidateQueries({ queryKey: ['ksef-connections'] });
    },
    onError: () =>
      toast.show({ title: t('tenant.ksefSchedule.toast.saveError'), variant: 'error' }),
  });

  return (
    <div className="mt-2 space-y-3 rounded-lg border border-border-primary bg-bg-primary p-3 text-sm">
      <label className="flex items-center gap-2 font-medium text-text-primary">
        <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
        {t('tenant.ksefSchedule.autoSync')}
      </label>

      {enabled && (
        <div className="space-y-3 pl-6">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label className={labelCls}>{t('tenant.ksefSchedule.frequency')}</label>
              <Dropdown
                value={String(interval)}
                onChange={(val) => setInterval(Number(val))}
                options={INTERVAL_OPTIONS.map((o) => ({
                  value: String(o.value),
                  label: t(o.labelKey),
                }))}
                className="w-full"
                aria-label={t('tenant.ksefSchedule.frequency')}
              />
            </div>
            <div>
              <label className={labelCls}>{t('tenant.ksefSchedule.fromDate')}</label>
              <DatePicker
                value={fromDate ? fromDate.slice(0, 10) : ''}
                onChange={setFromDate}
                className="w-full"
                aria-label={t('tenant.ksefSchedule.fromDate')}
              />
            </div>
          </div>

          <div>
            <span className={labelCls}>{t('tenant.ksefSchedule.directions')}</span>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 text-text-primary">
                <input
                  type="checkbox"
                  checked={types.includes('purchase')}
                  onChange={() => toggleType('purchase')}
                />
                {t('tenant.ksefSchedule.purchase')}
              </label>
              <label className="flex items-center gap-2 text-text-primary">
                <input
                  type="checkbox"
                  checked={types.includes('sales')}
                  onChange={() => toggleType('sales')}
                />
                {t('tenant.ksefSchedule.sales')}
              </label>
            </div>
          </div>

          <div>
            <span className={labelCls}>{t('tenant.ksefSchedule.hourWindow')}</span>
            <div className="flex items-center gap-2">
              <span className="text-text-secondary">{t('tenant.ksefSchedule.from')}</span>
              <Dropdown
                value={hourFrom}
                onChange={setHourFrom}
                options={[
                  { value: '', label: '—' },
                  ...Array.from({ length: 24 }, (_, h) => ({
                    value: String(h),
                    label: `${String(h).padStart(2, '0')}:00`,
                  })),
                ]}
                className="w-auto"
                aria-label={t('tenant.ksefSchedule.from')}
              />
              <span className="text-text-secondary">{t('tenant.ksefSchedule.to')}</span>
              <Dropdown
                value={hourTo}
                onChange={setHourTo}
                options={[
                  { value: '', label: '—' },
                  ...Array.from({ length: 24 }, (_, h) => h + 1).map((h) => ({
                    value: String(h),
                    label: `${String(h).padStart(2, '0')}:00`,
                  })),
                ]}
                className="w-auto"
                aria-label={t('tenant.ksefSchedule.to')}
              />
              <span className="text-xs text-text-muted">{t('tenant.ksefSchedule.allDay')}</span>
            </div>
          </div>

          <div>
            <span className={labelCls}>{t('tenant.ksefSchedule.weekdays')}</span>
            <div className="flex flex-wrap gap-1.5">
              {WEEKDAYS.map((d) => {
                const on = weekdays.includes(d.value);
                return (
                  <button
                    key={d.value}
                    type="button"
                    onClick={() => toggleWeekday(d.value)}
                    className={`rounded-md border px-2.5 py-1 text-xs font-medium ${
                      on
                        ? 'border-accent-600 bg-accent-600 text-white'
                        : 'border-border-primary text-text-secondary hover:bg-bg-tertiary'
                    }`}
                  >
                    {t(d.labelKey)}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      <div className="flex justify-end border-t border-border-primary pt-2">
        <Button
          variant="primary"
          size="sm"
          loading={saveMutation.isPending}
          onClick={() => saveMutation.mutate()}
        >
          {t('tenant.ksefSchedule.save')}
        </Button>
      </div>
    </div>
  );
}

export default KsefSyncScheduleEditor;
